import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { ICONS, LOGOS } from '../../constants/imageConstants';
import { APP_NAME, APP_VERSION } from '../../constants/appConstants';

const AboutPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isLoggedIn = !!user;

  const features = [
    {
      icon: (
        <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
      title: 'Data Collection',
      description: 'Comprehensive survey tools for field data gathering'
    },
    {
      icon: (
        <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Mobile Optimized',
      description: 'Designed for seamless field operations'
    },
    {
      icon: (
        <svg className="w-8 h-8 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      title: 'Offline Capability',
      description: 'Continuous operation without connectivity'
    },
    {
      icon: (
        <svg className="w-8 h-8 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Advanced Analytics',
      description: 'Comprehensive reporting and insights'
    }
  ];

  const metrics = [
    { value: '2.5K+', label: 'Surveys Completed' },
    { value: '180+', label: 'Active Projects' },
    { value: '99.2%', label: 'Data Accuracy' },
    { value: '85+', label: 'Field Teams' }
  ];

  const handleAuthAction = () => {
    navigate(isLoggedIn ? '/dashboard' : '/login');
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2.5 rounded-xl hover:bg-slate-50 transition-colors duration-200">
              <img src={ICONS.BACK} alt="Back" className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-slate-900">About Us</h1>
            </div>
          </div>
          <button
            onClick={handleAuthAction}
            className="hidden md:flex items-center gap-2 px-8 py-3 bg-white border-2 border-emerald-600 text-emerald-600 font-semibold rounded-lg hover:bg-emerald-50 transition-colors duration-200"
          >
            {isLoggedIn ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h7v7H3V3zm11 0h7v7h-7V3zM3 14h7v7H3v-7zm11 0h7v7h-7v-7z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
            )}
            {isLoggedIn ? 'Dashboard' : 'Login'}
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-slate-50 rounded-2xl border border-slate-200 mb-8">
            <img src={LOGOS.NGO_BLUE} alt="Logo" className="w-12 h-12" />
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">{APP_NAME}</h2>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-full border border-slate-200 mb-8">
            <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
            <span className="text-sm font-medium text-slate-600">v{APP_VERSION}</span>
          </div>

          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-4xl mx-auto">
            Enterprise-grade field data collection platform engineered for precision, reliability, and seamless operations across diverse environments.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-20">
          {metrics.map((metric, index) => (
            <div key={index} className="bg-slate-50 rounded-2xl p-4 sm:p-8 text-center border border-slate-100 hover:border-slate-200 transition-colors">
              <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{metric.value}</div>
              <div className="text-slate-600 font-medium text-sm sm:text-base">{metric.label}</div>
            </div>
          ))}
        </div>

        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Platform Capabilities</h3>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Comprehensive toolset designed for modern field operations and data-driven decision making.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <div key={index} className="group">
                <div className="bg-white border border-slate-200 rounded-xl p-6 hover:border-slate-300 transition-colors">
                  <div className="flex justify-center mb-4 group-hover:scale-110 transition-transform duration-200">{feature.icon}</div>
                  <h4 className="text-lg font-semibold text-slate-900 mb-3 text-center">{feature.title}</h4>
                  <p className="text-slate-600 text-center leading-relaxed text-sm sm:text-base">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-900 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center text-white mb-16">
          <h3 className="text-2xl sm:text-3xl font-bold mb-6">Built for Scale</h3>
          <p className="text-slate-300 text-lg max-w-3xl mx-auto mb-10">
            Trusted by organizations worldwide for mission-critical data collection and analysis. Engineered with enterprise-grade security and performance standards.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="px-4 sm:px-4 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <span className="font-medium text-sm sm:text-base">Enterprise Security</span>
            </div>
            <div className="px-4 sm:px-4 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <span className="font-medium text-sm sm:text-base">Real-time Sync</span>
            </div>
            <div className="px-4 sm:px-4 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <span className="font-medium text-sm sm:text-base">Cloud Integration</span>
            </div>
          </div>
        </div>
      </main>

      <footer className="w-full">
        <div className="bg-slate-50 py-6 border-t border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-6 h-6 bg-slate-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <span className="text-slate-900 font-semibold">NGO Field Data Foundation</span>
            </div>
            <p className="text-slate-600 text-sm">&copy; {new Date().getFullYear()} NGO Field Data Foundation. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;